import moment from 'moment'

import { FOLLOWER_PROJECTS_TYPE } from '../../../components/Followers/FollowerConstants'
import { BatchWrapper } from '../../../functions/BatchWrapper/batchWrapper'
import { getDb, getId, tryAddFollower } from '../firestore'
import {
    createProjectAssistantChangedFeed,
    createProjectColorChangedFeed,
    createProjectEstimationTypeChangedFeed,
    createProjectSharedChangedFeed,
    createProjectTitleChangedFeed,
} from './projectUpdates'
import store from '../../../redux/store'
import ProjectHelper from '../../../components/SettingsView/ProjectsSettings/ProjectHelper'
import { ALL_USERS } from '../../../components/GoalsView/GoalsHelper'
import {
    GOAL_MILESTONES_MODE_LINEAR,
    MILESTONE_TYPE_LINEAR,
    getCurrentTimezone,
    getLinearMilestonePeriods,
    getLinearMilestoneTitle,
    normalizeGoalMilestonesConfig,
} from '../../GoalMilestonesHelper'
import { normalizeTaskGoalRoutingMode } from '../../TaskGoalSuggestion'

async function getLinearMilestoneByPeriodKey(projectId, ownerId, periodKey) {
    const doc = (
        await getDb()
            .collection(`goalsMilestones/${projectId}/milestonesItems`)
            .where('ownerId', '==', ownerId)
            .where('done', '==', false)
            .where('periodKey', '==', periodKey)
            .limit(1)
            .get()
    ).docs[0]
    return doc ? { id: doc.id, ...doc.data() } : null
}

async function ensureProjectLinearMilestones(projectId, ownerId, config) {
    const periods = getLinearMilestonePeriods(config, Date.now(), config.futureMilestonesToCreate + 1)

    for (let i = 0; i < periods.length; i++) {
        const period = periods[i]
        const existingMilestone = await getLinearMilestoneByPeriodKey(projectId, ownerId, period.periodKey)
        if (!existingMilestone) {
            await getDb()
                .collection(`goalsMilestones/${projectId}/milestonesItems`)
                .doc(getId())
                .set({
                    extendedName: getLinearMilestoneTitle(period),
                    created: Date.now(),
                    date: period.date,
                    done: false,
                    assigneesCapacityDates: {},
                    doneDate: Date.now(),
                    hasStar: '#FFFFFF',
                    ownerId,
                    milestoneType: MILESTONE_TYPE_LINEAR,
                    periodStartDate: period.periodStartDate,
                    periodEndDate: period.periodEndDate,
                    periodKey: period.periodKey,
                    cadence: period.cadence,
                })
        }
    }
}

export async function setProjectIsShared(project, newShared) {
    getDb().doc(`projects/${project.id}`).update({ isShared: newShared })

    const batch = new BatchWrapper(getDb())
    await createProjectSharedChangedFeed(project.id, project, newShared, project.isShared, batch)
    const followProjectData = {
        followObjectsType: FOLLOWER_PROJECTS_TYPE,
        followObjectId: project.id,
        followObject: project,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(project.id, followProjectData, batch)
    batch.commit()
}

export async function setProjectEstimationType(project, newType) {
    getDb().doc(`projects/${project.id}`).update({ estimationType: newType })

    const batch = new BatchWrapper(getDb())
    await createProjectEstimationTypeChangedFeed(project.id, project, newType, project.estimationType, batch)
    const followProjectData = {
        followObjectsType: FOLLOWER_PROJECTS_TYPE,
        followObjectId: project.id,
        followObject: project,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(project.id, followProjectData, batch)
    batch.commit()
}

export async function setProjectName(project, newTitle) {
    getDb().doc(`projects/${project.id}`).update({ name: newTitle })

    const batch = new BatchWrapper(getDb())
    await createProjectTitleChangedFeed(project.id, project, project.name, newTitle, batch)
    const followProjectData = {
        followObjectsType: FOLLOWER_PROJECTS_TYPE,
        followObjectId: project.id,
        followObject: project,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(project.id, followProjectData, batch)
    batch.commit()
}

export async function setProjectColor(project, newColor) {
    getDb().doc(`projects/${project.id}`).update({ color: newColor })

    const batch = new BatchWrapper(getDb())
    await createProjectColorChangedFeed(project.id, project, project.color, newColor, batch)
    const followProjectData = {
        followObjectsType: FOLLOWER_PROJECTS_TYPE,
        followObjectId: project.id,
        followObject: project,
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(project.id, followProjectData, batch)
    batch.commit()
}

export function setProjectLastChatActionDate(projectId) {
    getDb().doc(`/projects/${projectId}`).update({
        lastChatActionDate: moment().valueOf(),
    })
}

export function setProjectAutoEstimation(projectId, autoEstimation) {
    getDb().doc(`/projects/${projectId}`).update({
        autoEstimation,
    })
}

export function setProjectTaskGoalRoutingMode(projectId, taskGoalRoutingMode) {
    return getDb()
        .doc(`/projects/${projectId}`)
        .update({
            taskGoalRoutingMode: normalizeTaskGoalRoutingMode(taskGoalRoutingMode),
        })
}

export async function setProjectGoalMilestonesConfig(projectId, config) {
    const normalizedConfig = normalizeGoalMilestonesConfig(config, getCurrentTimezone())
    await getDb().doc(`/projects/${projectId}`).update({
        goalMilestonesConfig: normalizedConfig,
    })

    if (normalizedConfig.mode === GOAL_MILESTONES_MODE_LINEAR) {
        const { loggedUser } = store.getState()
        const project = ProjectHelper.getProjectById(projectId)
        const ownerId = project?.parentTemplateId ? loggedUser.uid : ALL_USERS
        await ensureProjectLinearMilestones(projectId, ownerId, normalizedConfig)
    }
}

export function setProjectDayRateTimeLog(projectId, dayRateTimeLog) {
    getDb().doc(`/projects/${projectId}`).update({
        dayRateTimeLog,
    })
}

export function setProjectSortIndex(projectId, userId, sortIndex, batch) {
    batch.update(getDb().doc(`/projects/${projectId}`), {
        [`sortIndexByUser.${userId}`]: sortIndex,
    })
}

export const setProjectAssistant = async (projectId, assistantId, needGenerateUpdate) => {
    console.log('🎨 setProjectAssistant called:', { projectId, assistantId, needGenerateUpdate })

    getDb().doc(`projects/${projectId}`).update({ assistantId })

    const batch = new BatchWrapper(getDb())

    if (needGenerateUpdate) {
        await createProjectAssistantChangedFeed(projectId, batch)
    }

    const followProjectData = {
        followObjectsType: FOLLOWER_PROJECTS_TYPE,
        followObjectId: projectId,
        followObject: ProjectHelper.getProjectById(projectId),
        feedCreator: store.getState().loggedUser,
    }
    await tryAddFollower(projectId, followProjectData, batch)
    await batch.commit()

    // Automatically set as default if this is the user's default project
    const { loggedUser } = store.getState()
    const isDefaultProject = assistantId && loggedUser?.defaultProjectId === projectId

    console.log('🔍 Checking if should auto-set as default:', {
        projectId,
        assistantId,
        defaultProjectId: loggedUser?.defaultProjectId,
        isDefaultProject,
    })

    if (isDefaultProject) {
        const { projectAssistants, globalAssistants } = store.getState()
        const isProjectAssistant = projectAssistants[projectId]?.some(a => a.uid === assistantId)
        const isGlobalAssistant = globalAssistants?.some(a => a.uid === assistantId)

        console.log('🔎 Assistant type check:', {
            assistantId,
            isProjectAssistant,
            isGlobalAssistant,
        })

        if (isProjectAssistant) {
            // Check if it's already set as default to avoid redundant updates
            const assistant = projectAssistants[projectId]?.find(a => a.uid === assistantId)
            if (!assistant?.isDefault) {
                // For project assistants, set isDefault: true in the document
                console.log('✨ Auto-setting project assistant as default')
                const { setAssistantLikeDefault } = require('../Assistants/assistantsFirestore')
                setAssistantLikeDefault(projectId, assistantId)
            } else {
                console.log('ℹ️  Assistant is already set as default, skipping redundant update')
            }
        } else if (isGlobalAssistant) {
            // For global assistants, just setting project.assistantId is enough
            // The getDefaultAssistant function will pick it up from there
            console.log('✨ Global assistant set as project assistant (will be used as default)')
        } else {
            console.log('⚠️  Unknown assistant type')
        }
    } else {
        console.log('ℹ️  Not auto-setting as default (not the default project)')
    }
}

// Helper function to generate project prefix from project name
export function generateProjectPrefix(projectName) {
    if (!projectName || projectName.length === 0) {
        return 'TA' // Default fallback
    }

    // Remove non-alphabetic characters and get first and last letters
    const cleanName = projectName.replace(/[^a-zA-Z]/g, '')

    if (cleanName.length === 0) {
        return 'TA' // Fallback if no letters found
    }

    // Get first and last letters, convert to uppercase
    let prefix
    if (cleanName.length === 1) {
        prefix = cleanName + 'A' // If only one letter, append 'A'
    } else {
        prefix = (cleanName[0] + cleanName[cleanName.length - 1]).toUpperCase()
    }
    return prefix
}

// Initialize task ID counter for a project if it doesn't exist
export async function initializeProjectTaskCounter(projectId) {
    const projectRef = getDb().doc(`projects/${projectId}`)

    try {
        await getDb().runTransaction(async transaction => {
            const projectDoc = await transaction.get(projectRef)

            if (projectDoc.exists && !projectDoc.data().hasOwnProperty('taskIdCounter')) {
                transaction.update(projectRef, { taskIdCounter: 0 })
            }
        })
    } catch (error) {
        console.error('Error initializing task counter for project:', projectId, error)
    }
}

// Generate next task ID for a project and increment counter
export async function getNextTaskId(projectId) {
    const projectRef = getDb().doc(`projects/${projectId}`)

    try {
        return await getDb().runTransaction(async transaction => {
            const projectDoc = await transaction.get(projectRef)

            if (!projectDoc.exists) {
                throw new Error(`Project ${projectId} not found`)
            }

            const projectData = projectDoc.data()
            const currentCounter = projectData.taskIdCounter || 0
            const nextCounter = currentCounter + 1

            // Update the counter
            transaction.update(projectRef, { taskIdCounter: nextCounter })

            // Generate the human-readable ID
            const prefix = generateProjectPrefix(projectData.name)
            const humanReadableId = `${prefix}-${nextCounter}`

            return humanReadableId
        })
    } catch (error) {
        console.error('Error generating task ID for project:', projectId, error)

        // Fallback strategy: try to get project data for prefix, use timestamp for number
        try {
            const projectDoc = await getDb().doc(`projects/${projectId}`).get()
            if (projectDoc.exists) {
                const prefix = generateProjectPrefix(projectDoc.data().name)
                const timestamp = Date.now().toString().slice(-4)
                return `${prefix}-${timestamp}`
            }
        } catch (fallbackError) {
            console.error('Error in fallback ID generation:', fallbackError)
        }

        // Ultimate fallback
        const timestamp = Date.now().toString().slice(-4)
        return `TA-${timestamp}`
    }
}

/**
 * The project's browsing configuration (the `browser_automation` tool's allowlist).
 *
 * Stored on the project document rather than on the assistant because a browsing allowlist is a
 * property of the workspace: two assistants in one project must not be able to disagree about which
 * sites may be opened. It is written whole rather than merged so removing the last entry actually
 * removes it — a merge would leave the old array in place and the site would stay allowed.
 *
 * Client-written and therefore untrusted: `functions/Assistant/browser/browserAllowlist.js`
 * re-validates every entry on every read, and no entry can widen anything beyond "may be opened".
 */
export function setProjectBrowserAutomation(projectId, browserAutomation) {
    if (!projectId) return Promise.reject(new Error('setProjectBrowserAutomation needs a project id'))
    // `set` with merge rather than `update`: this is a settings write on a document the caller may
    // be the first to touch, and `update` rejects with `not-found` when the document is missing
    // while writing nothing. Merge touches only this one field, so it is otherwise identical.
    return getDb().doc(`projects/${projectId}`).set({ browserAutomation }, { merge: true })
}
